import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlask, faPenToSquare, faXRay } from '@fortawesome/free-solid-svg-icons';

const DoctorContextPanel = ({
    myLabRequests = [],
    otherLabRequests = [],
    myRadiologyRequests = [],
    otherRadiologyRequests = [],
    onViewReport,
    onEnterRadiologyResults
}) => {
    const [activeTab, setActiveTab] = useState('labs');

    const current = useMemo(() => {
        if (activeTab === 'labs') {
            return {
                my: myLabRequests,
                others: otherLabRequests,
                label: 'lab'
            };
        }
        return {
            my: myRadiologyRequests,
            others: otherRadiologyRequests,
            label: 'radiology'
        };
    }, [activeTab, myLabRequests, otherLabRequests, myRadiologyRequests, otherRadiologyRequests]);

    const totalCurrentRequests = current.my.length + current.others.length;

    const renderRequestItem = (req, section = 'my') => {
        const patient = req.Patient || {};
        const patientName = `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim();
        const isComplete = req.status === 'complete';
        const requestedBy = req.requested_by || 'Unknown';

        return (
            <div key={req.id} className="lab-request-item">
                <div className="lab-request-info">
                    {isComplete && onViewReport ? (
                        <span
                            className="lab-patient-name lab-patient-clickable"
                            onClick={() => onViewReport(req.id)}
                            title="View Report"
                        >
                            {patientName}
                        </span>
                    ) : (
                        <span className="lab-patient-name">{patientName}</span>
                    )}
                    {section === 'others' && (
                        <div className="lab-requested-by">
                            {requestedBy}{req.requested_branch ? ` · ${req.requested_branch}` : ''}
                        </div>
                    )}
                </div>
                {activeTab === 'radiology' && onEnterRadiologyResults && (
                    <button
                        className="lab-request-action-btn"
                        type="button"
                        onClick={() => onEnterRadiologyResults(req)}
                        title="Enter radiology results"
                        aria-label="Enter radiology results"
                    >
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                )}
                <span className={`status-chip status-${(req.status || 'pending').toLowerCase()}`}>
                    {req.status || 'Pending'}
                </span>
            </div>
        );
    };

    return (
        <div className="doctor-context-panel">
            <div className="context-tabs">
                <button
                    className={`context-tab ${activeTab === 'labs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('labs')}
                >
                    <FontAwesomeIcon icon={faFlask} /> Labs ({myLabRequests.length + otherLabRequests.length})
                </button>
                <button
                    className={`context-tab ${activeTab === 'radiology' ? 'active' : ''}`}
                    onClick={() => setActiveTab('radiology')}
                >
                    <FontAwesomeIcon icon={faXRay} /> Radiology ({myRadiologyRequests.length + otherRadiologyRequests.length})
                </button>
            </div>

            <div className="context-content">
                {totalCurrentRequests === 0 ? (
                    <div className="context-empty">
                        <p>No {current.label} requests for today</p>
                    </div>
                ) : (
                    <>
                        <div className="context-subsection-title">My Requests ({current.my.length})</div>
                        {current.my.length === 0 ? (
                            <div className="context-subsection-empty">No requests</div>
                        ) : (
                            current.my.map((req) => renderRequestItem(req, 'my'))
                        )}

                        <div className="context-subsection-title">Others ({current.others.length})</div>
                        {current.others.length === 0 ? (
                            <div className="context-subsection-empty">No requests</div>
                        ) : (
                            current.others.map((req) => renderRequestItem(req, 'others'))
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DoctorContextPanel;
